import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  BackgroundJob,
  backgroundPhotoUploads,
} from '../../../utils/backgroundPhotoUploads';

/**
 * SW2-2458. One notice for every background photo upload (finding and site
 * photos: progress, then done or failed with Retry), and a leave-page prompt while
 * photos are still on this device. Mounted once, in App.
 */

const TOAST_ID = 'background-photos';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * SW2-2458: a job sends one photo per request, so it can fail with some photos already on the
 * server; the job's error says how many (`landedPhotos`).
 */
const landedPhotos = (job: BackgroundJob) =>
  Math.min(job.photoCount, Math.max(0, Number((job.error as any)?.landedPhotos) || 0));

/** A failed job's photos that are not on the server (or may not be). */
const unsentPhotos = (job: BackgroundJob) => job.photoCount - landedPhotos(job);

function errorText(error: unknown): string {
  const err = error as any;

  return (
    err?.response?.data?.message ||
    // Cut off mid-request: the server may already have the photos, and nothing
    // lets a Retry tell a second copy apart (no client id on the upload API).
    (err?.request && !err?.response
      ? err?.notSent
        ? 'The connection dropped.'
        : 'The connection dropped. Some may already have uploaded: check before Retry.'
      : '') ||
    err?.message ||
    'Something went wrong.'
  );
}

const retryFailed = () =>
  backgroundPhotoUploads
    .getJobs()
    .filter((job) => job.status === 'failed')
    .forEach((job) => backgroundPhotoUploads.retry(job.id));

/** Forget finished jobs once the success notice closes. */
const clearDone = () =>
  backgroundPhotoUploads
    .getJobs()
    .filter((job) => job.status === 'done')
    .forEach((job) => backgroundPhotoUploads.dismiss(job.id));

/**
 * A failed job holds the only copy of its photos (and, for a closure save, the
 * edit itself), so giving up is an explicit, confirmed action, never a side
 * effect of closing the notice.
 */
/**
 * The Discard confirmation. A request cut off mid-upload may have reached the
 * server before its answer was lost (there is no client id to tell), so "not
 * uploaded" is only said when no failure could have been sent.
 */
export function discardPrompt(failed: BackgroundJob[]): string {
  const count = failed.reduce((sum, job) => sum + unsentPhotos(job), 0);
  const landed = failed.reduce((sum, job) => sum + landedPhotos(job), 0);
  const kept = landed > 0 ? ` (${plural(landed, 'photo')} already uploaded and will stay.)` : '';
  const maybeOnServer = failed.some((job) => {
    const err = job.error as any;

    return Boolean(err?.uncertain || (err?.request && !err?.response && !err?.notSent));
  });

  return maybeOnServer
    ? `Discard ${plural(count, 'photo')}? The copies on this device will be lost. Some may already have uploaded before the connection dropped.${kept}`
    : `Discard ${plural(count, 'photo')}? They have not been uploaded and will be lost.${kept}`;
}

const discardFailed = () => {
  const failed = backgroundPhotoUploads
    .getJobs()
    .filter((job) => job.status === 'failed');

  if (!window.confirm(discardPrompt(failed))) {
    return;
  }

  failed.forEach((job) => backgroundPhotoUploads.dismiss(job.id));
};

const RetryButton: React.FC<{ withDiscard?: boolean }> = ({ withDiscard }) => (
  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
    <button type='button' className='button tiny' onClick={retryFailed}>
      Retry
    </button>
    {withDiscard ? (
      <button type='button' className='button tiny outline' onClick={discardFailed}>
        Discard
      </button>
    ) : null}
  </div>
);

/** The notice's text and kind for the current jobs, or null when there are none. */
export function summarize(jobs: readonly BackgroundJob[]) {
  if (jobs.length === 0) return null;

  const photosIn = (list: BackgroundJob[]) =>
    list.reduce((sum, job) => sum + job.photoCount, 0);
  const active = jobs.filter((j) => j.status === 'queued' || j.status === 'uploading');
  const failed = jobs.filter((j) => j.status === 'failed');
  const done = jobs.filter((j) => j.status === 'done');
  const total = photosIn([...jobs]);
  // A failed job's photos that landed before it failed are uploaded too (SW2-2458: one per request).
  const uploaded = photosIn(done) + failed.reduce((sum, job) => sum + landedPhotos(job), 0);
  const unsent = failed.reduce((sum, job) => sum + unsentPhotos(job), 0);

  if (active.length > 0) {
    const current = active.find((j) => j.status === 'uploading');
    const sent = uploaded + (current ? current.photoCount * current.progress : 0);

    // Each job's photos go up in one request, so "sent" within it is estimated
    // from the bytes; rounding down keeps the count from running ahead.
    return {
      kind: 'uploading' as const,
      text: `Uploading ${Math.floor(sent)} of ${total} photos (${Math.round(
        (sent / total) * 100,
      )}%)`,
      failedPhotos: unsent,
    };
  }

  if (failed.length > 0) {
    const landed = failed.reduce((sum, job) => sum + landedPhotos(job), 0);

    return {
      kind: 'failed' as const,
      text: `${plural(unsent, 'photo')} did not upload${
        landed > 0 ? ` (${landed} did)` : ''
      }. ${errorText(failed[failed.length - 1].error)}`,
      failedPhotos: unsent,
    };
  }

  return { kind: 'done' as const, text: `${plural(uploaded, 'photo')} uploaded`, failedPhotos: 0 };
}

function show(jobs: readonly BackgroundJob[]) {
  const summary = summarize(jobs);

  if (!summary) {
    toast.dismiss(TOAST_ID);

    return;
  }

  let content: React.ReactNode = summary.text;
  let options: any;

  if (summary.kind === 'uploading') {
    // Browsers show only their own generic wording when the tab is closed, so
    // say it here: moving around the app is fine, closing or reloading is not.
    content = (
      <div>
        <div>{summary.text}</div>
        <div style={{ fontSize: '0.85em', opacity: 0.8 }}>
          Keep this tab open until they finish.
        </div>
        {summary.failedPhotos ? (
          <>
            <div>{plural(summary.failedPhotos, 'photo')} failed.</div>
            <RetryButton />
          </>
        ) : null}
      </div>
    );
    options = { type: toast.TYPE.DEFAULT, isLoading: true, autoClose: false, closeButton: false };
  } else if (summary.kind === 'failed') {
    // Stays up until the user retries or closes it.
    content = (
      <div>
        <div>{summary.text}</div>
        <div style={{ fontSize: '0.85em', opacity: 0.8 }}>
          They are only on this device until they upload.
        </div>
        <RetryButton withDiscard />
      </div>
    );
    // No close button: Retry or Discard are the only ways out.
    options = {
      type: toast.TYPE.ERROR,
      isLoading: false,
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
    };
  } else {
    options = {
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 4000,
      closeButton: true,
      onClose: clearDone,
    };
  }

  if (toast.isActive(TOAST_ID)) {
    toast.update(TOAST_ID, { ...options, render: content });
  } else {
    toast(content, { ...options, toastId: TOAST_ID });
  }
}

/** Failed jobs that never reached the server (offline when they ran). */
const retryNotSent = () =>
  backgroundPhotoUploads
    .getJobs()
    .filter((job) => job.status === 'failed' && (job.error as any)?.notSent)
    .forEach((job) => backgroundPhotoUploads.retry(job.id));

const BackgroundUploadsNotifier: React.FC = () => {
  useEffect(() => backgroundPhotoUploads.subscribe(show), []);

  // A phone locks its screen after a while, and iOS then suspends the page and
  // cuts the upload off. Keep the screen awake while anything is uploading
  // (Screen Wake Lock: iOS Safari 16.4+, Chrome on Android; ignored elsewhere).
  useEffect(() => {
    const nav: any = navigator;
    let lock: any = null;
    let requesting = false;
    let unmounted = false;

    const isBusy = () =>
      backgroundPhotoUploads
        .getJobs()
        .some((job) => job.status === 'queued' || job.status === 'uploading');

    const sync = async () => {
      const busy = isBusy();

      if (busy && !lock && !requesting && nav.wakeLock && document.visibilityState === 'visible') {
        requesting = true;
        try {
          const granted = await nav.wakeLock.request('screen');

          // The last job may have settled (or the page unmounted) while the
          // request was pending; nothing else would release it then.
          if (unmounted || !isBusy()) {
            granted.release().catch(() => undefined);
          } else {
            lock = granted;
            lock.addEventListener('release', () => {
              lock = null;
            });
          }
        } catch {
          lock = null; // Refused (low battery, unsupported): carry on without it.
        } finally {
          requesting = false;
        }
      } else if (!busy && lock) {
        lock.release().catch(() => undefined);
        lock = null;
      }
    };
    // The lock is dropped whenever the page is hidden; take it again on return,
    // and retry anything that failed only because the device was offline.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (navigator.onLine) retryNotSent();
      sync();
    };

    const unsubscribe = backgroundPhotoUploads.subscribe(() => {
      sync();
    });

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', retryNotSent);

    return () => {
      unmounted = true;
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', retryNotSent);
      if (lock) lock.release().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Failed jobs count too: their photos exist only on this device.
      if (backgroundPhotoUploads.pending().length === 0) return undefined;

      // Browsers show their own wording; this text is only used by old ones.
      const message = 'Photos are still uploading. Leave anyway?';

      e.preventDefault();
      e.returnValue = message;

      return message;
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  return null;
};

export default BackgroundUploadsNotifier;
